import React from 'react';

/* ─── Severity colours ─── */
export const SEVERITY_COLORS = {
  Blocker:     '#ef4444',
  Critical:    '#f97316',
  Major:       '#eab308',
  Minor:       '#22c55e',
  Enhancement: '#3b82f6',
  Unspecified: '#6b7280',
};
// All five severities always shown in legend (in display order)
const SEVERITY_LEGEND_ORDER = ['Blocker', 'Critical', 'Major', 'Minor', 'Enhancement'];
const colorFor = (sev) => SEVERITY_COLORS[sev] || '#6b7280';

/*
 * SeverityPie — SVG pie chart matching the expected "Open Issue Severity Count" design.
 *  • Title at top
 *  • Callout labels outside the pie: "SeverityName: XX.X % (count)"
 *  • Legend row at the bottom
 */
export const SeverityPie = ({ slices = [] }) => {
  const total = slices.reduce((s, x) => s + x.count, 0);
  if (total === 0) return <div style={{ color: '#999', fontSize: 13, padding: 8 }}>No data</div>;

  /* ── SVG layout constants ── */
  const W       = 720;   // viewBox width (plenty of horizontal space for callouts)
  const H       = 450;   // viewBox height (leaves clear separation from bottom legend)
  const CX      = W / 2; // 360 - pie centre x
  const CY      = 235;   // pie centre y
  const R       = 130;   // pie radius
  const ELBOW_R = R + 45; // radius for elbow bend
  const HORIZ   = 25;   // horizontal tick length

  /* ── Build arc + label data ── */
  const nonZero = slices.filter((s) => s.count > 0);
  const single  = nonZero.length === 1;

  let angle = -Math.PI / 2; // start at 12 o'clock
  const arcs = nonZero.map((s) => {
    const frac  = s.count / total;
    const start = angle;
    const end   = angle + frac * 2 * Math.PI;
    const mid   = (start + end) / 2;
    angle = end;

    /* Arc path */
    const sx = CX + R * Math.cos(start);
    const sy = CY + R * Math.sin(start);
    const ex = CX + R * Math.cos(end);
    const ey = CY + R * Math.sin(end);
    const large = (end - start) > Math.PI ? 1 : 0;
    const d = `M ${CX} ${CY} L ${sx} ${sy} A ${R} ${R} 0 ${large} 1 ${ex} ${ey} Z`;

    /* Leader line: start on pie edge, elbow at ELBOW_R, horizontal tick */
    const p1x = CX + (R + 4) * Math.cos(mid);
    const p1y = CY + (R + 4) * Math.sin(mid);
    
    // Adjust elbow to keep vertical displacement balanced for top/bottom labels
    let p2x = CX + ELBOW_R * Math.cos(mid);
    let p2y = CY + ELBOW_R * Math.sin(mid);

    const isRight = Math.cos(mid) >= 0;
    const p3x = p2x + (isRight ? HORIZ : -HORIZ);
    const p3y = p2y;
    const textX  = p3x + (isRight ? 6 : -6);
    const anchor = isRight ? 'start' : 'end';

    const pct   = s.percent != null ? s.percent : Math.round((s.count / total) * 1000) / 10;
    const label = `${s.severity}: ${pct}% (${s.count})`;
    const color = colorFor(s.severity);

    return { d, color, key: s.severity, p1x, p1y, p2x, p2y, p3x, p3y, textX, anchor, label };
  });

  return (
    <div style={{ background: '#fff', width: '100%', maxWidth: 720, margin: '0 auto' }}>
      <svg
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        style={{ display: 'block', overflow: 'hidden' }}
      >
        {/* ── Title ── */}
        <text
          x={CX}
          y={32}
          textAnchor="middle"
          fontWeight="bold"
          fontSize="20"
          fill="#111"
          fontFamily="sans-serif"
        >
          Open Issue Severity Count
        </text>

        {/* ── Pie slices ── */}
        {single ? (
          <circle cx={CX} cy={CY} r={R} fill={colorFor(nonZero[0].severity)} stroke="#fff" strokeWidth="2" />
        ) : (
          arcs.map((a) => (
            <path key={a.key} d={a.d} fill={a.color} stroke="#fff" strokeWidth="2" />
          ))
        )}

        {/* ── Colored leader lines + labels ── */}
        {arcs.map((a) => (
          <g key={`lbl-${a.key}`}>
            {/* Diagonal: pie edge → elbow */}
            <line x1={a.p1x} y1={a.p1y} x2={a.p2x} y2={a.p2y} stroke={a.color} strokeWidth="1.5" />
            {/* Horizontal tick */}
            <line x1={a.p2x} y1={a.p2y} x2={a.p3x} y2={a.p3y} stroke={a.color} strokeWidth="1.5" />
            {/* Label text */}
            <text
              x={a.textX}
              y={a.p3y + 4}
              textAnchor={a.anchor}
              fontSize="13"
              fontWeight="500"
              fill="#222"
              fontFamily="sans-serif"
            >
              {a.label}
            </text>
          </g>
        ))}
      </svg>

      {/* ── Legend bottom — filled circles ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '12px 24px',
        padding: '10px 0 16px',
        background: '#fff',
        borderTop: '1px solid #f0f0f0'
      }}>
        {SEVERITY_LEGEND_ORDER.map((sev) => (
          <span key={sev} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#333', fontFamily: 'sans-serif' }}>
            <span style={{
              display: 'inline-block',
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: SEVERITY_COLORS[sev],
              flexShrink: 0
            }} />
            {sev}
          </span>
        ))}
      </div>
    </div>
  );
};

/* ─── Status colours (Open / Confirm / Done) ─── */
const STATUS_COLOR = {
  Open:    '#ef4444',   // red
  Confirm: '#eab308',   // yellow
  Done:    '#22c55e',   // green
};

/*
 * DomainBars — Excel-style stacked bar chart matching the expected UI.
 *
 * Stack order (bottom→top): Done(green) → Confirm(yellow) → Open(red)
 * Table shown directly below the bars (column-aligned with each bar).
 */
export const DomainBars = ({ bars = [] }) => {
  if (!bars || bars.length === 0) {
    return <div className="text-gray-500 text-sm py-4">No domain data available</div>;
  }

  /* ── Layout constants ── */
  const n       = bars.length;
  // Ensure each bar column has at least 60px width so text and bars never crowd/overlap
  const slotW   = Math.max(60, Math.floor(720 / Math.max(n, 1)));
  const PLOT_W  = slotW * n;
  const PAD_L   = 70;           // left padding (matches first table column width)
  const PAD_R   = 20;
  const CHART_W = PLOT_W + PAD_L + PAD_R;
  const CHART_H = 260;          // SVG height
  const PAD_TOP = 40;           // top padding for title
  const PAD_BOT = 15;           // bottom padding (table connects directly below X-axis)
  const PLOT_H  = CHART_H - PAD_TOP - PAD_BOT;

  /* ── Y-axis scale ── */
  const maxVal = Math.max(...bars.map((b) => b.total), 1);
  const rawStep = maxVal / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep || 1))) || 1;
  const niceStep = Math.ceil(rawStep / magnitude) * magnitude || 1;
  const yMax = niceStep * 6;
  const gridLines = Array.from({ length: 7 }, (_, i) => i * niceStep).reverse();

  /* ── Bar geometry ── */
  const barGap = 0.35;
  const barW = Math.max(12, slotW * (1 - barGap));
  const barOffset = (slotW - barW) / 2;

  const toY = (v) => PLOT_H - (v / yMax) * PLOT_H;
  const toH = (v) => (v / yMax) * PLOT_H;

  return (
    <div className="w-full overflow-x-auto" style={{ background: '#fff', padding: '8px 0' }}>
      <div style={{ width: CHART_W, margin: '0 auto' }}>
        {/* ── SVG chart ── */}
        <svg
          width={CHART_W}
          height={CHART_H}
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          style={{ display: 'block', background: '#fff' }}
        >
          {/* Title */}
          <text
            x={CHART_W / 2}
            y={22}
            textAnchor="middle"
            fontWeight="bold"
            fontSize="18"
            fill="#111"
            fontFamily="sans-serif"
          >
            Issue Domain
          </text>

          {/* "Count" Y-axis label */}
          <text
            x={14}
            y={PAD_TOP + PLOT_H / 2}
            textAnchor="middle"
            transform={`rotate(-90, 14, ${PAD_TOP + PLOT_H / 2})`}
            fontSize="12"
            fill="#555"
            fontFamily="sans-serif"
          >
            Count
          </text>

          {/* ── Grid lines + Y-axis tick labels ── */}
          {gridLines.map((v) => {
            const y = PAD_TOP + toY(v);
            return (
              <g key={v}>
                <line
                  x1={PAD_L}
                  y1={y}
                  x2={PAD_L + PLOT_W}
                  y2={y}
                  stroke="#e0e0e0"
                  strokeWidth="1"
                  strokeDasharray="4 4"
                />
                <text
                  x={PAD_L - 8}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill="#666"
                  fontFamily="sans-serif"
                >
                  {v}
                </text>
              </g>
            );
          })}

          {/* ── Axes ── */}
          <line x1={PAD_L} y1={PAD_TOP} x2={PAD_L} y2={PAD_TOP + PLOT_H} stroke="#999" strokeWidth="1" />
          <line x1={PAD_L} y1={PAD_TOP + PLOT_H} x2={PAD_L + PLOT_W} y2={PAD_TOP + PLOT_H} stroke="#999" strokeWidth="1" />

          {/* ── Stacked Bars ── */}
          {bars.map((b, i) => {
            const open    = b.open    ?? 0;
            const confirm = b.confirm ?? 0;
            const done    = b.close   ?? 0;
            const total   = open + confirm + done;

            const x = PAD_L + i * slotW + barOffset;
            const baseY = PAD_TOP + PLOT_H;

            const doneH    = toH(done);
            const confirmH = toH(confirm);
            const openH    = toH(open);

            const doneY    = baseY - doneH;
            const confirmY = doneY - confirmH;
            const openY    = confirmY - openH;

            const cx = x + barW / 2;

            return (
              <g key={b.domain}>
                {/* Done (green) */}
                {doneH > 0 && (
                  <rect x={x} y={doneY} width={barW} height={doneH} fill={STATUS_COLOR.Done} />
                )}
                {/* Confirm (yellow) */}
                {confirmH > 0 && (
                  <rect x={x} y={confirmY} width={barW} height={confirmH} fill={STATUS_COLOR.Confirm} />
                )}
                {/* Open (red) */}
                {openH > 0 && (
                  <rect x={x} y={openY} width={barW} height={openH} fill={STATUS_COLOR.Open} />
                )}
                {/* Total label above bar */}
                {total > 0 && (
                  <text
                    x={cx}
                    y={openY - 4}
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="bold"
                    fill="#333"
                    fontFamily="sans-serif"
                  >
                    {total}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* ── Data table (directly below bars, column-aligned with slotW) ── */}
        <table
          style={{
            width: CHART_W,
            borderCollapse: 'collapse',
            fontSize: 11,
            background: '#fff',
            tableLayout: 'fixed',
          }}
        >
          <colgroup>
            <col style={{ width: PAD_L }} />
            {bars.map((b) => <col key={b.domain} style={{ width: slotW }} />)}
            <col style={{ width: PAD_R }} />
          </colgroup>
          <thead>
            <tr style={{ borderTop: '2px solid #999', borderBottom: '1px solid #ccc' }}>
              <th style={{ padding: '6px 4px', textAlign: 'left', background: '#f8f8f8', color: '#555' }}></th>
              {bars.map((b) => (
                <th
                  key={b.domain}
                  style={{
                    padding: '6px 2px',
                    textAlign: 'center',
                    background: '#f8f8f8',
                    fontWeight: 600,
                    color: '#222',
                    borderLeft: '1px solid #e0e0e0',
                    verticalAlign: 'bottom',
                    fontSize: 11,
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                  }}
                  title={b.domain}
                >
                  {b.domain.replace(/:\s*/g, ':\n')}
                </th>
              ))}
              <th style={{ background: '#f8f8f8' }}></th>
            </tr>
          </thead>
          <tbody>
            {[
              { label: 'Open',    key: 'open',    color: STATUS_COLOR.Open },
              { label: 'Confirm', key: 'confirm', color: STATUS_COLOR.Confirm },
              { label: 'Done',    key: 'close',   color: STATUS_COLOR.Done },
            ].map(({ label, key, color }, rowIdx) => (
              <tr
                key={label}
                style={{ borderBottom: '1px solid #eee', background: rowIdx % 2 === 0 ? '#fff' : '#fafafa' }}
              >
                <td style={{ padding: '4px 6px', whiteSpace: 'nowrap', fontWeight: 600, fontSize: 11 }}>
                  <span style={{ color, marginRight: 4 }}>●</span>
                  <span style={{ color: '#333' }}>{label}</span>
                </td>
                {bars.map((b) => (
                  <td
                    key={b.domain}
                    style={{
                      padding: '5px 2px',
                      textAlign: 'center',
                      color: '#444',
                      borderLeft: '1px solid #eee',
                      fontSize: 11,
                    }}
                  >
                    {b[key] ?? 0}
                  </td>
                ))}
                <td></td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── Legend ── */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 24,
          padding: '10px 0 6px',
          background: '#fff',
        }}>
          {[
            { label: 'Done',    color: STATUS_COLOR.Done },
            { label: 'Confirm', color: STATUS_COLOR.Confirm },
            { label: 'Open',    color: STATUS_COLOR.Open },
          ].map(({ label, color }) => (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#333', fontFamily: 'sans-serif' }}>
              <span style={{
                display: 'inline-block',
                width: 14,
                height: 14,
                background: color,
                borderRadius: 2,
              }} />
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
